import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import { useNavigate, useParams } from 'react-router';
import { CustomerInfo, GoogleMapsButton } from '../../components/customer-info';
import { OrderedProducts } from '../../components/ordered-products/ordered-products';
import MapContainer from '../../../Map/containers/MapContainer';
import NOB from '../../../../constants/language-strings';
import Status, { COMPLETE, INCOMPLETE } from '../../../../constants/status-codes';
import { TechnicalDetails } from '../../components/technical-details/technical-details';
import { TECHNICIAN, EXTERNAL_INSTALLER } from '../../../Authentication/constants';
import { WAITING_ACTIVATION, SCHEDULED_ACTIVATION } from '../../../../constants/productStatuses';
import UNAUTHORIZED from '../../../../constants/errorStatusCodes';
import { ChangeStatusButton } from '../change-status-button/change-status-button';
import { workorderServicesRequest } from '../../../WorkLog/actions/workOrderServiceActions';
import NavigationButton from '../../../../components/Buttons/NavigationButton/navigation-button';
import { ADDRESS_HISTORY_URL, HEALTH_CHECK_URL, CUSTOMER_PRODUCTS_URL, WORK_LOG_URL, NOT_HOME_URL, WORK_ORDER_HISTORY_URL, } from '../../../../constants/paths';
import { Icon } from '@purpurds/purpur/icon';
import { icons } from 'theme/icons';
import { WorkOrderDetailsContainer } from '../../components/work-order-details/work-order-details-container';
import { Loader, PurPurButton } from 'components';
import styled from 'styled-components';
import { purpurColorRed600, purpurColorPurple800 } from '@purpurds/tokens/color/variables';
import TextBox from 'modules/Workorder/components/text-box/text-box';
import { useWorkOrderPage } from 'modules/Workorder/api';
import { ErrorNotification } from 'components/error-notification';
import { Modal } from 'components/modal/modal';
const mapContainerHeight = `calc(320px + 66px)`;
const WorkOrderDetails = styled.div `
  display: flex;
  justify-content: center;
`;
const WorkOrderContainer = styled.div `
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
`;
const WorkOrderContent = styled.div `
  display: flex;
  flex-direction: column;
  justify-content: stretch;
  width: 100%;
  min-width: 320px;
  max-width: 750px;
  position: relative;

  @media (min-width: 750px) {
    width: 750px;
  }
`;
const WorkOrderContentTop = styled.div `
  height: calc(100% + ${mapContainerHeight});
  padding-bottom: ${mapContainerHeight};
`;
const CustomerInfoContainer = styled.div `
  display: block;
  width: 100%;
  margin-bottom: 5px;
  margin-top: 0.5rem;
`;
const WorkOrderContentDescription = styled.div `
  display: flex;
  margin-bottom: 10px;

  @media (min-width: 750px) {
    display: flex;
    flex-direction: row;
  }

  @media (max-width: 750px) {
    display: flex;
    flex-direction: column;
  }
`;
const StyledIcon = styled(Icon) `
  color: ${purpurColorPurple800};
`;
const NotHomeButton = styled.div `
  margin-bottom: 20px;
  width: 100%;
  text-align: center;

  a {
    color: ${purpurColorRed600};
    border-bottom: solid 1px ${purpurColorRed600};
    letter-spacing: normal;
    line-height: normal;
    font-size: 20px;
  }
`;
const ButtonWrapper = styled.div `
  display: flex;
  justify-content: center;
  gap: 1rem;
`;
const WorkOrderPage = ({ role = EXTERNAL_INSTALLER, workOrderId = 0, orderedItems, requestWorkOrderServices, }) => {
    var _a;
    const { id } = useParams();
    const navigate = useNavigate();
    const [completed, setCompleted] = useState(false);
    const [googleMapsModalOpen, setGoogleMapsModalOpen] = useState(null);
    const { workOrder, addressHistory, customerProducts, isLoading, workOrderErrorMessage, addressHistoryError, } = useWorkOrderPage(workOrderId);
    useEffect(() => {
        requestWorkOrderServices(workOrderId);
    }, [workOrderId, requestWorkOrderServices]);
    const handleClickFinish = useCallback(() => {
        setCompleted(prev => !prev);
    }, []);
    const newWorkOrderEntered = !workOrderId || id !== `${workOrderId}`;
    const noAccess = workOrderErrorMessage.includes(UNAUTHORIZED);
    if (isLoading && !workOrderErrorMessage) {
        return _jsx(Loader, {});
    }
    if (workOrderErrorMessage) {
        return (_jsx(ErrorNotification, { title: NOB.FEEDBACKS.SORRY, link: noAccess, text: noAccess ? NOB.NO_WORK_ORDERS.NO_ACCESS : workOrderErrorMessage }));
    }
    if (!workOrder || workOrder.workOrderId === 0) {
        return (_jsx(ErrorNotification, { title: NOB.FEEDBACKS.SORRY, link: true, text: "Unable to retrieve work order information. Try to refresh browser or login again." }));
    }
    const technician = role === TECHNICIAN;
    const allocated = Status.ALLOCATED.includes(workOrder.status);
    const onSite = Status.ON_SITE.includes(workOrder.status);
    const isTraveling = Status.TRAVELING.includes(workOrder.status);
    const done = COMPLETE.includes(workOrder.status) || INCOMPLETE.includes(workOrder.status);
    const installerNotDone = !workOrder.isThreeSixty && !done;
    const installerNotDoneOrOnSite = installerNotDone || onSite;
    const waitingActivationsLength = customerProducts === null || customerProducts === void 0 ? void 0 : customerProducts.filter(waitingActivation => waitingActivation.status === WAITING_ACTIVATION ||
        waitingActivation.status === SCHEDULED_ACTIVATION).length;
    return (_jsxs("div", { children: [_jsx(WorkOrderDetails, { children: _jsx(WorkOrderDetailsContainer, { done: done, workOrder: workOrder, totalTimeEstimate: orderedItems.totalTimeEstimate, inWorkLog: false }) }), _jsx(WorkOrderContainer, { children: _jsxs(WorkOrderContent, { children: [_jsxs(WorkOrderContentTop, { children: [workOrder.isThreeSixty && (isTraveling || allocated) && (_jsx(ChangeStatusButton, { workOrder: workOrder, isFetching: isLoading })), isTraveling && (_jsx(NotHomeButton, { children: _jsx(Link, { to: NOT_HOME_URL(workOrderId), children: NOB.FOLLOW_UP.NOT_HOME }) })), _jsx(CustomerInfoContainer, { children: _jsx(CustomerInfo, { address: workOrder.address, addressDetails: workOrder.addressDetails, customer: workOrder.customer, openModal: (address) => setGoogleMapsModalOpen(address), workOrder: workOrder }) }), !done && (!!(addressHistory === null || addressHistory === void 0 ? void 0 : addressHistory.length) || !!addressHistoryError) && (_jsx(NavigationButton, { URL: ADDRESS_HISTORY_URL(workOrderId), leftIcon: _jsx(StyledIcon, { svg: icons.map }), title: NOB.WORKORDER_TABS.ADDRESS_HISTORY, number: addressHistory === null || addressHistory === void 0 ? void 0 : addressHistory.length })), _jsxs(WorkOrderContentDescription, { children: [_jsx(TextBox, { heading: NOB.COMMENT.DESCRIPTION_HEADING, text: workOrder.workDescription, attachments: workOrder.attachments, languageStrings: NOB.COMMENT }), _jsx(OrderedProducts, { done: done, orderedItems: orderedItems, technician: technician })] }), _jsx(NavigationButton, { URL: WORK_ORDER_HISTORY_URL(workOrderId), id: workOrderId, title: NOB.WORKORDER_HISTORY.WORKORDER_HISTORY, leftIcon: _jsx(StyledIcon, { svg: icons.summary }) }), _jsx(NavigationButton, { URL: CUSTOMER_PRODUCTS_URL(workOrderId), id: workOrder.customer.customerId, title: NOB.CUSTOMER_PRODUCTS.CUSTOMER_PRODUCTS, leftIcon: _jsx(StyledIcon, { svg: icons.box }), number: waitingActivationsLength || null }), _jsx(NavigationButton, { URL: HEALTH_CHECK_URL(workOrderId), leftIcon: _jsx(StyledIcon, { svg: icons.pulse }), title: NOB.HEALTH_CHECK.HEALTH_CHECK }), _jsx(TechnicalDetails, { technicalDetails: workOrder.technicalDetails, capacity: (_a = workOrder === null || workOrder === void 0 ? void 0 : workOrder.capacity) === null || _a === void 0 ? void 0 : _a.data }), done && (_jsx(TextBox, { heading: NOB.COMMENT.COMMENT, text: workOrder.actionTaken, languageStrings: NOB.COMMENT }))] }), !!googleMapsModalOpen && (_jsx(Modal, { title: NOB.GOOGLE_MAPS_POPUP.GOOGLE_MAPS, isOpen: !!googleMapsModalOpen, onClose: () => setGoogleMapsModalOpen(null), style: 'info', actionButtons: _jsxs(ButtonWrapper, { children: [_jsx(PurPurButton, { variant: "secondary", onClick: () => setGoogleMapsModalOpen(null), children: "Cancel" }), _jsx(GoogleMapsButton, { onClose: () => setGoogleMapsModalOpen(null), address: googleMapsModalOpen })] }), children: NOB.GOOGLE_MAPS_POPUP.QUESTION })), ((technician && onSite) || !technician) && (_jsx(MapContainer, { newWorkOrderEntered: newWorkOrderEntered, customerAddress: workOrder.address, technicalDetails: workOrder.technicalDetails })), installerNotDoneOrOnSite && (_jsx(NavigationButton, { URL: WORK_LOG_URL(workOrderId), title: NOB.WORK_LOG.COMPLETE_WORKORDER_BUTTON, isCompleteWorkOrder: true, onClick: (e) => {
                                if ((waitingActivationsLength !== null && waitingActivationsLength !== void 0 ? waitingActivationsLength : 0) > 0) {
                                    e.preventDefault();
                                    handleClickFinish();
                                }
                            } }))] }) }), completed && (_jsx(Modal, { title: NOB.WORK_LOG.POPUP_TITLE, isOpen: completed, onClose: () => setCompleted(false), style: 'info', actionButtons: _jsxs(ButtonWrapper, { children: [_jsx(PurPurButton, { variant: "secondary", onClick: () => setCompleted(false), children: NOB.WORK_LOG.POPUP_CANCEL_BUTTON }), _jsx(PurPurButton, { variant: 'primary', onClick: () => {
                                navigate(WORK_LOG_URL(workOrderId));
                            }, children: NOB.WORK_LOG.POPUP_COMPLETE_BUTTON })] }), children: NOB.WORK_LOG.POPUP_TEXT })), 
            // TODO: Hva er det her?
            !(COMPLETE.includes(/\d/.test(workOrder.status)
                ? workOrder.status.slice(workOrder.status.lastIndexOf(' ') + 1)
                : workOrder.status) ||
                INCOMPLETE.includes(/\d/.test(workOrder.status)
                    ? workOrder.status.slice(workOrder.status.lastIndexOf(' ') + 1)
                    : workOrder.status))] }));
};
const mapStateToProps = (state) => ({
    role: state.authentication.role,
    orderedItems: state.workOrderServices.orderedItems,
});
const mapDispatchToProps = (dispatch) => ({
    requestWorkOrderServices: (workOrderId) => dispatch(workorderServicesRequest(workOrderId)),
});
export default connect(mapStateToProps, mapDispatchToProps)(WorkOrderPage);
